import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa";
import {
  AssetGalleryManager,
  EmptyState,
  PageHeader,
  SectionCard,
  StatCard,
} from "components/admin";
import { CrudForm, type CrudFieldConfig } from "components/admin/workspace-crud";
import RightDrawer from "components/global/RightDrawer";
import { buildPlayerProfileFields } from "pages/app/workspace/sections/forms/athlete-section-forms";
import {
  PrimaryActionButton,
  SectionStatsGrid,
} from "pages/app/workspace/sections/styles/workspace-section.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDateTime, getApiErrorMessage } from "utils/admin";
import { hasPortalCapability } from "utils/portal-access";
import {
  PlayerProfileSectionGridLayout,
  PlayerProfileSectionGridLayoutSecondary,
  PlayerProfileSectionSection,
  PlayerProfileSectionSectionSecondary,
  PlayerProfileSectionHeroImage,
  PlayerProfileSectionEyebrowText,
  PlayerProfileSectionTitleText,
} from "pages/app/workspace/sections/styles/player-profile-section.inline.styles";
export const PlayerProfileSection: React.FC<{
  currentUser: any;
}> = ({ currentUser }) => {
  const queryClient = useQueryClient();
  const playerId = currentUser?.player?.id;
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const canManageOwnProfile = hasPortalCapability(
    currentUser?.portal_access,
    "can_manage_own_player_profile",
  );
  const { data: athlete } = useQuery({
    queryKey: ["workspace-player-profile", playerId],
    queryFn: () => workspaceApi.athletes.get(playerId),
    enabled: Boolean(playerId),
  });
  const { data: teams = [] } = useQuery({
    queryKey: ["workspace-player-profile-teams"],
    queryFn: () => workspaceApi.teams.list(),
    enabled: canManageOwnProfile,
  });
  const updateMutation = useMutation({
    mutationFn: (payload: any) => workspaceApi.athletes.update(playerId, payload),
    onSuccess: () => {
      toast.success("Athlete profile updated.");
      setDrawerOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["workspace-player-profile", playerId],
      });
      queryClient.invalidateQueries({
        queryKey: ["athlete", playerId],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to update this athlete profile."));
    },
  });
  const profileFields: CrudFieldConfig[] = buildPlayerProfileFields(teams);
  if (!playerId) {
    return (
      <>
        <PageHeader
          title="My Athlete Profile"
          description="This section becomes available when the current user has a linked athlete record."
        />
        <EmptyState
          description="The current account does not have a linked player profile in the API yet."
          title="No linked athlete profile"
        />
      </>
    );
  }
  return (
    <>
      <PageHeader
        title="My Athlete Profile"
        description="Review the current athlete record returned by the API and update the base player fields when your access profile allows it."
        actions={
          canManageOwnProfile ? (
            <PrimaryActionButton onClick={() => setDrawerOpen(true)} type="button">
              <FaPlus size={12} />
              Edit profile
            </PrimaryActionButton>
          ) : null
        }
      />

      <SectionStatsGrid>
        <StatCard
          label="Athlete"
          value={athlete?.full_name || currentUser?.player?.full_name || "Unknown"}
        />
        <StatCard
          label="Position"
          value={athlete?.position || currentUser?.player?.position || "Unknown"}
        />
        <StatCard label="Followers" value={athlete?.follower_count ?? 0} />
        <StatCard label="Credits" value={athlete?.in_app_credits ?? 0} />
      </SectionStatsGrid>

      <PlayerProfileSectionGridLayout>
        <SectionCard subtitle="Current athlete media" title="Profile">
          <PlayerProfileSectionHeroImage
            alt={athlete?.full_name || "Athlete"}
            fallbackLabel={athlete?.full_name}
            src={athlete?.avatar_url || athlete?.image}
            variant="athlete"
          />
        </SectionCard>

        <SectionCard subtitle="Base player record returned by the API" title="Details">
          <PlayerProfileSectionGridLayoutSecondary>
            {[
              ["Full name", athlete?.full_name || currentUser?.player?.full_name || "Unknown"],
              ["Position", athlete?.position || currentUser?.player?.position || "Unknown"],
              ["Country", athlete?.country || athlete?.profile?.country || "Unknown"],
              [
                "Primary team",
                athlete?.primary_team?.name || athlete?.team_names?.[0] || "Unknown",
              ],
              ["Sports", (athlete?.sport_names || []).join(", ") || "Unknown"],
              ["Created", formatDateTime(athlete?.created_at)],
            ].map(([label, value]) => (
              <PlayerProfileSectionSection key={String(label)}>
                <PlayerProfileSectionEyebrowText>{label}</PlayerProfileSectionEyebrowText>
                <PlayerProfileSectionTitleText>{value}</PlayerProfileSectionTitleText>
              </PlayerProfileSectionSection>
            ))}
          </PlayerProfileSectionGridLayoutSecondary>
        </SectionCard>
      </PlayerProfileSectionGridLayout>

      <PlayerProfileSectionSectionSecondary>
        <AssetGalleryManager
          canManage={canManageOwnProfile && Boolean(playerId)}
          createLabel="Add gallery asset"
          emptyDescription="Your athlete gallery is still empty."
          invalidateKeys={[
            ["workspace-player-profile", playerId],
            ["athlete", playerId],
          ]}
          items={athlete?.gallery_items || []}
          onCreate={
            playerId
              ? (payload) => workspaceApi.galleryItems.createPlayer(playerId, payload)
              : undefined
          }
          onRemove={(item) => workspaceApi.galleryItems.remove(item.id as string)}
          onUpdate={(item, payload) => workspaceApi.galleryItems.update(item.id as string, payload)}
          subtitle="Use the new player gallery API to curate photos, posters, clips, and supporting assets for the linked athlete profile."
          title="Player Gallery"
        />
      </PlayerProfileSectionSectionSecondary>

      <RightDrawer
        isOpen={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Edit athlete profile"
      >
        <CrudForm
          fields={profileFields}
          initialValues={{
            first_name: athlete?.first_name || currentUser?.player?.first_name || "",
            last_name: athlete?.last_name || currentUser?.player?.last_name || "",
            position: athlete?.position || currentUser?.player?.position || "",
            team_id: athlete?.primary_team?.id || currentUser?.player?.team_id || "",
          }}
          isSubmitting={updateMutation.isPending}
          onCancel={() => setDrawerOpen(false)}
          onSubmit={(payload) => updateMutation.mutate(payload)}
          submitLabel="Save athlete profile"
        />
      </RightDrawer>
    </>
  );
};
